import { useEffect } from 'react';

import {
  Checkbox,
  Divider,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Box,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { TextFieldWithLabel } from 'src/components/atoms/textField';
import { CPDEnrollmentForm } from 'src/helpers/constants';
import {
  allowOnlyNumber,
  allowOnlyThai,
  scrollToElement,
  validateAllowOnlyThai,
  validateNotAllowSpace,
} from 'src/helpers/utils';
import { ICPDProfile } from 'src/interfaces';

import { Title } from '../title';

import { WarningContainer } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import MaskedCitizenIdInput from '@components/atoms/input/CitizenIdInput';

type Props = {
  isEmailValid: boolean;
  initEnrollmentData: CPDEnrollmentForm | null;
  citizenId?: string | number | null;
  email?: string | null;
  profile?: ICPDProfile;
};

const prefixList = ['นาย', 'นางสาว', 'นาง', 'น.ส.', 'นส.', 'น.ส'];

const Form = ({ isEmailValid, initEnrollmentData, citizenId, email, profile }: Props) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext();

  const prefix = profile?.prefix;
  useEffect(() => {
    if (!initEnrollmentData) {
      if (citizenId) {
        setValue('citizenId', citizenId);
      }
      if (email) {
        setValue('email', email);
      }
      if (profile?.firstname) {
        setValue('firstName', profile?.firstname);
      }
      if (profile?.lastname) {
        setValue('lastName', profile?.lastname);
      }
      if (profile?.mobile) {
        setValue('mobile', profile?.mobile);
      }

      if (!prefix) return;

      if (!prefixList?.includes(prefix)) {
        setValue('inputPrefix', prefix);
        setValue('prefix', 'other');
      } else {
        const prefixForMiss = ['น.ส.', 'นส.', 'น.ส'].find((value) => value === prefix);
        setValue('prefix', prefixForMiss ? 'นางสาว' : prefix);
      }
    }
  }, []);

  const isPrefixOther = watch('prefix') === 'other';

  useEffect(() => {
    if (!isPrefixOther && watch('prefix') !== 'other') {
      setValue('inputPrefix', '');
    }
  }, [isPrefixOther]);

  return (
    <Grid container alignItems="baseline" spacing={2}>
      <Grid xs={12} item>
        <Typography variant="h7">
          <Typography variant="h7" component="span" color="errorDark.main">
            *
          </Typography>
          {t('prefix')}
        </Typography>
        <Controller
          rules={{ required: true }}
          control={control}
          name="prefix"
          render={({ field, fieldState: { invalid } }) => (
            <RadioGroup {...field}>
              <Grid container gap={{ lg: 0.3 }}>
                <Grid xs={4} md={2} lg={1.5} item>
                  <FormControlLabel
                    value="นาย"
                    control={<Radio size="small" sx={{ color: !isPrefixOther && invalid ? 'errorDark.main' : null }} />}
                    label={
                      <Typography variant="h7" fontFamily="Lanna">
                        {t('mister')}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid xs={4} md={2} lg={1.5} item>
                  <FormControlLabel
                    value="นาง"
                    control={<Radio size="small" sx={{ color: !isPrefixOther && invalid ? 'errorDark.main' : null }} />}
                    label={
                      <Typography variant="h7" fontFamily="Lanna">
                        {t('missus')}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid xs={4} md={2.1} lg={1.5} item>
                  <FormControlLabel
                    value="นางสาว"
                    control={<Radio size="small" sx={{ color: !isPrefixOther && invalid ? 'errorDark.main' : null }} />}
                    label={
                      <Typography variant="h7" fontFamily="Lanna">
                        {t('miss')}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid xs={4} md={2} lg={1.5} item zeroMinWidth>
                  <FormControlLabel
                    value="other"
                    control={<Radio size="small" sx={{ color: invalid ? 'errorDark.main' : null }} />}
                    label={
                      <Typography variant="h7" fontFamily="Lanna" noWrap>
                        {t('other_specific')}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid xs={8} md={3.9} lg={3.5} item>
                  <Controller
                    name="inputPrefix"
                    control={control}
                    rules={{
                      required: {
                        value: isPrefixOther,
                        message: t('error.please_fill_prefix'),
                      },
                      validate: {
                        always: (value) => {
                          if (validateAllowOnlyThai(value)) {
                            return `${t('error.please_fill_prefix')}${t('error.is_thai_language')}`;
                          }
                        },
                      },
                    }}
                    render={({ field, fieldState: { invalid, error } }) => {
                      if (error) {
                        scrollToElement('cpd_form_other_perfix');
                      }
                      return (
                        <TextFieldWithLabel
                          {...field}
                          id="cpd_form_other_perfix"
                          placeholder={t('please_specific')}
                          error={isPrefixOther && invalid}
                          helperText={isPrefixOther && error?.message}
                          disabled={!isPrefixOther}
                          onChange={(e) => field.onChange(allowOnlyThai(e.target.value))}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>

              <FormHelperText sx={{ color: 'errorDark.main', ml: '14px' }}>
                {invalid ? t('error.please_choose_prefix') : ''}
              </FormHelperText>
            </RadioGroup>
          )}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name="firstName"
          control={control}
          rules={{
            required: {
              value: true,
              message: t('error.please_fill_first_name'),
            },
            validate: {
              always: (value) => {
                if (validateNotAllowSpace(value)) {
                  return `${t('error.not_allow_space_in_first_name')}`;
                }
                if (validateAllowOnlyThai(value)) {
                  return `${t('error.please_fill_first_name')}${t('error.is_thai_language')}`;
                }
              },
              noSpace: (value) => (value && !!value?.trim()) || t('error.please_fill_first_name'),
            },
          }}
          render={({ field: { onChange, value, ref }, fieldState: { invalid, error } }) => {
            if (error) {
              scrollToElement('cpd_form_first_name');
            }
            return (
              <TextFieldWithLabel
                id="cpd_form_first_name"
                placeholder={t('fill_first_name')}
                error={invalid}
                helperText={error?.message}
                label={t('first_name')}
                required
                value={value}
                ref={ref}
                onChange={(e) => onChange(allowOnlyThai(e.target.value))}
              />
            );
          }}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name="lastName"
          control={control}
          rules={{
            required: {
              value: true,
              message: t('error.please_fill_last_name'),
            },
            validate: {
              always: (value) => {
                if (validateNotAllowSpace(value)) {
                  return `${t('error.not_allow_space_in_last_name')}`;
                }
                if (validateAllowOnlyThai(value)) {
                  return `${t('error.please_fill_last_name')}${t('error.is_thai_language')}`;
                }
              },
              noSpace: (value) => (value && !!value?.trim()) || t('error.please_fill_last_name'),
            },
          }}
          render={({ field: { onChange, value, ref }, fieldState: { invalid, error } }) => {
            if (error) {
              scrollToElement('cpd_form_last_name');
            }
            return (
              <TextFieldWithLabel
                id="cpd_form_last_name"
                placeholder={t('fill_last_name')}
                error={invalid}
                helperText={error?.message}
                label={t('last_name')}
                required
                value={value}
                ref={ref}
                onChange={(e) => onChange(allowOnlyThai(e.target.value))}
              />
            );
          }}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name="citizenId"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              {...field}
              label={t('citizen_id')}
              required
              disabled
              InputProps={{
                inputComponent: MaskedCitizenIdInput as any,
              }}
            />
          )}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name="email"
          control={control}
          render={({ field }) => <TextFieldWithLabel {...field} label={t('email')} required disabled />}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name="mobile"
          control={control}
          rules={{
            required: {
              value: true,
              message: t('error.please_fill_phone'),
            },
            pattern: {
              value: /^(0[689]{1})+([0-9]{8})+$/g,
              message: t('error.please_fill_correct_phone_number'),
            },
          }}
          render={({ field: { onChange, value, ref }, fieldState: { invalid, error } }) => {
            if (error) {
              scrollToElement('cpd_form_phone');
            }
            return (
              <TextFieldWithLabel
                id="cpd_form_phone"
                placeholder={`${t('example')} 0895339633`}
                error={invalid}
                helperText={error?.message}
                label={t('phone')}
                required
                inputProps={{
                  maxLength: 10,
                }}
                value={value}
                ref={ref}
                onChange={(e) => onChange(allowOnlyNumber(e.target.value))}
              />
            );
          }}
        />
      </Grid>
      {!isEmailValid && (
        <Grid xs={12} item>
          <WarningContainer>
            <Box>
              <FontAwesomeIcon icon="fa-light fa-circle-exclamation" />
            </Box>
            <Box>
              <Typography fontWeight="bold" variant="h7lanna" lineHeight="22px" component="p">
                {t('cpd_enrollment.email.warning.title')}
              </Typography>
              <Typography variant="h7lanna" color="rgba(0, 0, 0, 0.87)">
                {t('cpd_enrollment.email.warning.detail')}
              </Typography>
            </Box>
          </WarningContainer>
          <Controller
            name="email-consent"
            control={control}
            defaultValue={false}
            rules={{
              required: {
                value: true,
                message: t('cpd_enrollment.email.warning.consent_error'),
              },
            }}
            render={({ field, fieldState: { invalid, error } }) => {
              if (error) {
                scrollToElement('cpd_form_email_consent');
              }

              return (
                <>
                  <FormControlLabel
                    id="cpd_form_email_consent"
                    control={
                      <Checkbox
                        checked={field.value}
                        sx={{ color: invalid ? 'errorDark.main' : undefined }}
                        size="small"
                        {...field}
                      />
                    }
                    label={<Typography variant="h7lanna">{t('cpd_enrollment.email.warning.consent')}</Typography>}
                  />
                  {invalid && (
                    <FormHelperText sx={{ fontSize: '16px', color: 'errorDark.main', ml: '33px', mt: -1 }}>
                      {error?.message}
                    </FormHelperText>
                  )}
                </>
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

const PersonalInformation = ({ isEmailValid, initEnrollmentData, citizenId, email, profile }: Props) => {
  const { t } = useTranslation();
  return (
    <>
      <Title title={t('cpd_enrollment.personal_information')} subtitle={t('cpd_enrollment.please_verify_detail')} />
      <Divider sx={{ my: { xs: '24px', md: '30px' } }} />
      <Form
        isEmailValid={isEmailValid}
        initEnrollmentData={initEnrollmentData}
        citizenId={citizenId}
        email={email}
        profile={profile}
      />
    </>
  );
};

export default PersonalInformation;
